/**
 * Resolves a `data-tour` anchor to a live viewport rect.
 *
 * Measurement is observer-driven rather than a `requestAnimationFrame` loop:
 * rAF is paused entirely while the document is hidden, so a backgrounded tab
 * would resume with a stale spotlight, and a permanent 60fps loop costs battery
 * on the shop-floor handsets this runs on. Scroll/resize/mutation cover the
 * real movement, and a slow interval catches anything transition-driven.
 *
 * Returning `null` is a first-class outcome: a step whose screen has not been
 * built yet, or whose target has not mounted, degrades to a centred narrative
 * card instead of pointing at nothing.
 */
import { useEffect, useState } from 'react'

export interface TargetRect {
  top: number
  left: number
  width: number
  height: number
}

const SAME = (a: TargetRect | null, b: TargetRect | null): boolean => {
  if (a === null || b === null) return a === b
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height
}

const SAFETY_INTERVAL_MS = 2000

/** Nearest ancestor that actually scrolls, or null. */
function scrollableAncestor(el: Element): HTMLElement | null {
  let parent = el.parentElement
  while (parent) {
    const oy = window.getComputedStyle(parent).overflowY
    if ((oy === 'auto' || oy === 'scroll') && parent.scrollHeight > parent.clientHeight) {
      return parent
    }
    parent = parent.parentElement
  }
  // No scrollable ancestor — do not scroll the document.
  return null
}

/**
 * How long after a step opens we keep trying to bring its target into view, and
 * the minimum gap between attempts.
 *
 * Deliberately time-based rather than a plain attempt counter. `measure` is also
 * driven by a MutationObserver over the whole body, which fires in bursts during
 * a route transition — a counter gets spent entirely inside that burst, while the
 * layout is still moving, and the real attempt never happens. Throttling by time
 * gives a handful of well-spaced tries across the window instead, then stops so
 * this can never fight a learner scrolling on their own.
 */
const SCROLL_WINDOW_MS = 3000
const SCROLL_THROTTLE_MS = 250

/**
 * Bring `el` into view inside its nearest scrollable ancestor, retrying until
 * it is actually visible there.
 *
 * `scrollIntoView` on iOS WKWebView can propagate upward through the full
 * ancestor chain, which scrolls the document and makes the PracticeModeBar
 * and AppHeader visually shift. By walking up ourselves and stopping at the
 * first `overflow-y: auto | scroll` container we guarantee only the app's
 * inner list area moves, not the headers.
 *
 * This used to fire exactly once and latch, which lost the scroll in two common
 * cases: the container is not overflowing yet on the first measure (long forms),
 * or the list re-renders while the smooth scroll is in flight and `scrollTop`
 * snaps back to 0. Either way the target — and the coach-mark card pinned to it
 * — stayed below the fold for the whole step, with the card's Next button out of
 * reach. Retrying until the element is inside the container's box fixes both.
 *
 * Returns true when the element is already in view and nothing was done, so the
 * caller only counts attempts that actually scrolled.
 */
function ensureInView(el: Element, isRetry: boolean): boolean {
  const parent = scrollableAncestor(el)
  // Nothing scrollable yet — the container may not be overflowing until the rest
  // of the screen renders. Report "not in view" so the caller tries again.
  if (!parent) return false

  const parentRect = parent.getBoundingClientRect()
  const elRect = el.getBoundingClientRect()
  if (elRect.top >= parentRect.top && elRect.bottom <= parentRect.bottom) return true

  const targetScrollTop =
    parent.scrollTop + (elRect.top - parentRect.top) - (parentRect.height / 2 - elRect.height / 2)
  // Only the first attempt animates. A smooth scroll is cancelled and restarted
  // by every subsequent scrollTo, so retrying on a timer while still animating
  // kept resetting the animation and the container never actually moved — the
  // exact symptom this retry exists to fix. Retries jump instead.
  parent.scrollTo({
    top: Math.max(0, targetScrollTop),
    behavior: isRetry ? 'auto' : 'smooth',
  })
  return false
}

export function useTargetRect(target: string | undefined): TargetRect | null {
  const [rect, setRect] = useState<TargetRect | null>(null)

  useEffect(() => {
    if (!target) {
      setRect(null)
      return
    }

    let current: TargetRect | null = null

    const openedAt = Date.now()
    let lastScrollAt = 0
    let observed: Element | null = null
    // Observer callbacks are queued as microtasks, so one can still fire after
    // this effect has been torn down — and after the document itself is gone in
    // a test environment. Disconnecting is not enough on its own.
    let disposed = false

    // Feature-detected: jsdom has no ResizeObserver, and the interval below
    // already covers the element-resize case without it.
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure())

    /** Keep the ResizeObserver pointed at whatever element currently matches. */
    function reobserve(el: Element | null) {
      if (el === observed) return
      if (observed) resizeObserver?.unobserve(observed)
      if (el) resizeObserver?.observe(el)
      observed = el
    }

    /**
     * Scroll the target into view, at most once per throttle window and only
     * while the step is young.
     *
     * Deliberately not latched on a single "it was in view once" check: early
     * in a screen's life the target often sits inside the container simply
     * because the content above it has not rendered yet, and latching there
     * leaves it stranded once the real layout pushes it down. `measure` re-runs
     * on the SAFETY_INTERVAL_MS timer, so re-checking each tick and capping the
     * number of scrolls is both simpler and correct.
     */
    function maybeScroll(el: Element) {
      const now = Date.now()
      if (now - openedAt >= SCROLL_WINDOW_MS) return
      if (now - lastScrollAt < SCROLL_THROTTLE_MS) return
      if (!ensureInView(el, lastScrollAt !== 0)) lastScrollAt = now
    }

    /** The element's box, or null while it is mounted but not yet laid out. */
    function rectOf(el: Element | null): TargetRect | null {
      if (!el) return null
      const r = el.getBoundingClientRect()
      if (r.width <= 0 || r.height <= 0) return null
      maybeScroll(el)
      return { top: r.top, left: r.left, width: r.width, height: r.height }
    }

    // Track which node the MutationObserver is currently watching so we can
    // narrow it to the target element once it mounts and re-broaden if it unmounts.
    let observedNode: Node = document.body

    // Declared before measure() is first called so the two-phase reconnect
    // inside measure() can reference it without hitting the TDZ.
    const mutationObserver = new MutationObserver(() => measure())
    // Start broad — detect initial mount anywhere in the tree.
    mutationObserver.observe(document.body, { childList: true, subtree: true })

    function measure() {
      if (disposed || typeof document === 'undefined') return
      const el = document.querySelector(`[data-tour="${target}"]`)
      reobserve(el)
      const next = rectOf(el)
      if (!SAME(current, next)) {
        current = next
        setRect(next)
      }
      // Narrow the MutationObserver to the target element once it is in the DOM.
      // This cuts observed surface from the entire body tree to one element,
      // reducing mutation callback frequency on screens with large DOM trees.
      // Re-broadens automatically when the element unmounts.
      const targetNode: Node = el ?? document.body
      if (targetNode !== observedNode) {
        observedNode = targetNode
        mutationObserver.disconnect()
        if (el) {
          mutationObserver.observe(el, {
            childList: true,
            subtree: false,
            characterData: true,
            attributes: true,
          })
        } else {
          mutationObserver.observe(document.body, { childList: true, subtree: true })
        }
      }
    }

    measure()
    window.addEventListener('scroll', measure, { capture: true, passive: true })
    window.addEventListener('resize', measure, { passive: true })
    // visualViewport fires its own resize/scroll when the software keyboard
    // opens or closes — window resize does NOT fire on iOS Safari in that case.
    window.visualViewport?.addEventListener('resize', measure)
    window.visualViewport?.addEventListener('scroll', measure)
    const timer = window.setInterval(measure, SAFETY_INTERVAL_MS)

    return () => {
      disposed = true
      mutationObserver.disconnect()
      resizeObserver?.disconnect()
      window.removeEventListener('scroll', measure, { capture: true })
      window.removeEventListener('resize', measure)
      window.visualViewport?.removeEventListener('resize', measure)
      window.visualViewport?.removeEventListener('scroll', measure)
      window.clearInterval(timer)
    }
  }, [target])

  return rect
}
